import Button from "components/common/Button";
import CustomTable from "components/common/CustomTable";
import VendorAvatar from "components/common/VendorAvatar";
import { use } from "echarts";
import { formatMoney, getCurrencyFromCurrencyCode } from "helpers";
import { displayProductChannelIcon } from "helpers/displayProductChannelIcon";
import { truncate } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";

type SalesCanvasProps = {
    isExpanded: string;
    setIsExpanded: (isExpanded: string) => void;
    messageId?: string;
    salesOrderDetail?: Record<string, any>;
    summary?: string;
    toolName?: string;
    hideInlineExpand?: boolean; // Hide the built-in expand buttons when in Space context
};

const SalesCanvas = ({
    isExpanded,
    setIsExpanded,
    messageId,
    salesOrderDetail,
    toolName,
    summary,
    hideInlineExpand = false, // Default to false for Flow context
}: SalesCanvasProps) => {
    const tableHeader = [
        { title: "Product SKU/Name", widthClass: "w-[369px] ", titleClass: "text-slate-700" },
        { title: "Qty", widthClass: "w-[84px]", titleClass: "text-slate-700" },
        { title: "Unit price", widthClass: "w-[99px]", titleClass: "text-slate-700" },
        { title: "Total", widthClass: "w-[120px]", titleClass: "text-slate-700" },
        { title: "Available stock level", widthClass: "w-[197px]", titleClass: "text-slate-700" },
    ];

    const tableBody = salesOrderDetail?.products?.map((item, idx) => {
        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`py-3 pr-3 border-slate-100 text-sm text-gm-50 font-rocGroteskMedium `}
                    >
                        <div className="flex items-center gap-2">
                            <p className="border-r border-[#E2E8F0] text-sm text-gm-50 px-2">
                                {item?.sku}
                            </p>
                            <div className=" text-gm-50 flex items-center gap-2">
                                {item?.productImageDetails?.productAvatar ||
                                item?.productImageDetails?.productImages?.[0] ? (
                                    <VendorAvatar
                                        name={item?.productName}
                                        imageSrc={
                                            item?.productImageDetails?.productAvatar ||
                                            item?.productImageDetails?.productImages?.[0]
                                        }
                                        size={32}
                                        containerClassname="!rounded-none !border !bg-white"
                                    />
                                ) : (
                                    <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center ">
                                        <i className="ri-box-2-fill text-lg text-slate-500"></i>
                                    </div>
                                )}
                                <p className="text-sm font-medium">
                                    {truncate(item?.productName, { length: 30 })}
                                </p>
                            </div>
                        </div>
                    </div>
                ),
                widthClass: "!border-b",
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-gm-50 font-rocGroteskMedium `}
                    >
                        <p className=""> {item?.qty}</p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-gm-50 font-rocGroteskMedium `}
                    >
                        <p className="">
                            {" "}
                            {getCurrencyFromCurrencyCode(item?.unitPriceCurrency)}
                            {formatMoney().format(item?.unitPrice?.amount)}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-gm-50 font-rocGroteskMedium flex gap-2`}
                    >
                        <p className="">
                            {" "}
                            {getCurrencyFromCurrencyCode(item?.unitPriceCurrency)}
                            {formatMoney().format(item?.total)}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-gm-50 font-rocGroteskMedium flex gap-2`}
                    >
                        <p className=""> {formatMoney().format(item?.stockLevel)}</p>
                    </div>
                ),
                widthClass: "!border-b",
            },
        ];
    });

    const handleExpand = () => {
        setIsExpanded(messageId);
    };

    const handleCollapse = () => {
        setIsExpanded("");
    };

    return (
        <div className="font-rocGroteskMedium">
            <div className="border border-slate-200 rounded-xl">
                {!isExpanded && (
                    <div
                        className={`flex items-center justify-between gap-2 border-b border-b-slate-200 py-3 ${
                            !isExpanded ? "px-6" : "px-4"
                        }`}
                    >
                        <p className="text-[#303B41] text-base font-medium">
                            {isExpanded
                                ? "Sales order details"
                                : `Preview of sales order #SO-${
                                      salesOrderDetail?.taId ||
                                      salesOrderDetail?.orderId ||
                                      salesOrderDetail?._id
                                  }`}
                        </p>
                        {!hideInlineExpand && ( // Conditionally render based on prop
                            !isExpanded ? (
                                <div
                                    className="bg-slate-50 rounded-lg flex items-center justify-center text-sm px-3 py-1.5 gap-2 cursor-pointer"
                                    onClick={handleExpand}
                                >
                                    <i className="ri-picture-in-picture-exit-line text-slate-500"></i>
                                    <p className="text-slate-500 ">Expand canvas</p>
                                </div>
                            ) : (
                                <Button
                                    btnText="Collapse Canvas"
                                    btnClassname="!w-fit !text-g-75 !border-slate-200 !py-2.5 !px-4 !rounded-[20px] "
                                    icon={<i className="ri-picture-in-picture-exit-line text-g-75"></i>}
                                    onClick={handleCollapse}
                                />
                            )
                        )}
                    </div>
                )}
                <div
                    className={`   rounded-b-xl mt-4 relative ${
                        !isExpanded ? "bg-slate-50 px-6 pt-3" : ""
                    }`}
                >
                    <div className=" bg-white  px-4 py-3 rounded-xl">
                        <div className="flex items-center justify-between">
                            <p className="text-base text-g-75">Details</p>
                            <div className="flex items-center gap-2">
                                <p className="border-r border-[#E2E8F0] text-sm text-slate-500 px-2">
                                    Status
                                </p>
                                <div className="bg-[#F8BC581A] text-[#142837] flex items-center gap-2 py-[5px] px-[10px]">
                                    <div
                                        className={`w-2 h-2 rounded-full
                                            ${
                                                salesOrderDetail?.status === "fulfilled"
                                                    ? " bg-g-60"
                                                    : salesOrderDetail?.status === "in-progress"
                                                    ? " bg-b-55"
                                                    : salesOrderDetail?.status === "pending"
                                                    ? " bg-[#F4B000]"
                                                    : salesOrderDetail?.status === "cancelled"
                                                    ? " bg-[#E4281D]"
                                                    : " bg-b-55"
                                            }`}
                                    ></div>
                                    <p className="text-sm font-medium capitalize">
                                        {" "}
                                        {salesOrderDetail?.status}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="border border-[#E2E8F0] rounded-md py-6 px-6 grid grid-cols-4 gap-4 mt-4">
                            <div className="text-sm">
                                <p className="text-[#64748B] mb-6">Channel</p>
                                <div className="flex items-center space-x-2 ">
                                    {salesOrderDetail?.channel?.toLowerCase()?.includes("synth")
                                        ? salesOrderDetail?.customerDetails?.company ||
                                          (salesOrderDetail?.customerDetails?.first_name &&
                                          salesOrderDetail?.customerDetails?.last_name
                                              ? `${salesOrderDetail?.customerDetails?.first_name} ${salesOrderDetail?.customerDetails?.last_name}`
                                              : salesOrderDetail?.customerDetails?.first_name ||
                                                salesOrderDetail?.customerDetails?.last_name) ||
                                          salesOrderDetail?.customerDetails?.name ||
                                          ""
                                        : displayProductChannelIcon(
                                              salesOrderDetail?.channel,
                                              "!w-6 !h-6"
                                          )}

                                    <span className="captalize text-gm-50">
                                        {salesOrderDetail?.channel?.toLowerCase()?.includes("synth")
                                            ? ""
                                            : salesOrderDetail?.channel
                                                  ?.toLowerCase()
                                                  ?.includes("trackstar")
                                            ? salesOrderDetail?.channel?.split(".")[1]
                                            : salesOrderDetail?.channel}
                                    </span>
                                </div>
                            </div>
                            <div className="text-sm">
                                <p className="text-[#64748B] mb-6">Customer order number</p>
                                <div className="flex items-center gap-2">
                                    <p className="text-gm-50 break-all">
                                        {salesOrderDetail?.taId || salesOrderDetail?.orderId}
                                    </p>
                                </div>
                            </div>
                            <div className="text-sm">
                                <p className="text-[#64748B] mb-6">Order date</p>
                                <div className="flex items-center gap-2">
                                    <p className="text-gm-50">
                                        {salesOrderDetail?.saleDate
                                            ? moment(salesOrderDetail?.saleDate).format(
                                                  "MMM DD, YYYY"
                                              )
                                            : "Not Available"}
                                    </p>
                                </div>
                            </div>
                            <div className="text-sm">
                                <p className="text-[#64748B] mb-6">Delivery date</p>
                                <div className="flex items-center gap-2">
                                    <p className="text-gm-50">
                                        {salesOrderDetail?.deliveryDate
                                            ? moment(salesOrderDetail?.deliveryDate).format(
                                                  "MMM DD, YYYY"
                                              )
                                            : "Not Available"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 txet-sm text-gm-50 space-y-2">
                            <p className="">Customer details</p>
                            <div
                                className={`flex items-center gap-3 ${
                                    !isExpanded ? "opacity-50" : ""
                                }`}
                            >
                                <VendorAvatar
                                    name={
                                        salesOrderDetail?.customerDetails?.company ||
                                        `${salesOrderDetail?.customerDetails?.first_name || ""} ${
                                            salesOrderDetail?.customerDetails?.last_name || ""
                                        }`
                                    }
                                    size={24}
                                />

                                <p className="">
                                    {salesOrderDetail?.customerDetails?.company ||
                                        `${salesOrderDetail?.customerDetails?.first_name || ""} ${
                                            salesOrderDetail?.customerDetails?.last_name || ""
                                        }`}
                                </p>
                            </div>
                            <div
                                className={`flex items-center gap-5 ${
                                    !isExpanded ? "opacity-30" : ""
                                }`}
                            >
                                <i className="ri-mail-line text-slate-500"></i>
                                <p className="">{salesOrderDetail?.customerDetails?.email}</p>
                            </div>
                            <div
                                className={`flex items-center gap-5 ${
                                    !isExpanded ? "opacity-30" : ""
                                }`}
                            >
                                <i className="ri-phone-line text-slate-500"></i>
                                <p className="">{salesOrderDetail?.customerDetails?.phone}</p>
                            </div>
                        </div>

                        <div
                            className={`mt-4 border-t border-dashed border-[#E0E0E0] py-4 ${
                                !isExpanded ? "opacity-20" : ""
                            }`}
                        >
                            <p className="text-sm text-slate-500">Shipping destination</p>
                            <p className="text-sm text-gm-50">
                                {salesOrderDetail?.shipping?.address
                                    ? `${salesOrderDetail?.shipping?.address?.city || ""} ${
                                          salesOrderDetail?.shipping?.address?.country || ""
                                      }`
                                    : salesOrderDetail?.customerDetails?.address1 ||
                                      "Not Available"}
                            </p>
                        </div>

                        {isExpanded && (
                            <div className="">
                                {/* <div className="mt-4 border-t border-dashed border-[#E0E0E0] pt-4">
                                    <p className="text-sm text-slate-500">Approval levels</p>
                                    {Array.from({ length: 3 }).map((_, idx) => (
                                        <div
                                            className={`py-4 ${
                                                idx !== Array.from({ length: 3 }).length - 1
                                                    ? "border-b border-slate-100"
                                                    : ""
                                            }`}
                                        >
                                            <div className="flex items-center gap-3 text-xs mb-2">
                                                <div className="w-6 h-6 bg-slate-200 rounded-full"></div>
                                                <div className="">
                                                    <p className="">Garrett - Operations</p>
                                                    <p className="text-slate-500">
                                                        April 17, 2023; 2:00pm
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-gm-50 text-xs bg-slate-100 py-1 px-3 w-fit rounded">
                                                Level 1
                                            </div>
                                        </div>
                                    ))}
                                </div> */}
                                {/* table here */}
                                {salesOrderDetail?.products?.length > 0 && (
                                    <div className="border-t border-dashed border-[#E0E0E0] pt-4">
                                        <p className="text-g-75 font-rocGroteskMedium py-2 text-sm">
                                            Product details
                                        </p>
                                        <CustomTable
                                            tableBody={tableBody}
                                            tableHeader={tableHeader}
                                            isCellBordered={true}
                                            stickyHeader={true}
                                            headerContainerClass="!bg-slate-50"
                                            headerItemClass="font-rocGroteskMedium "
                                            tableClass="mb-4"
                                            isScrollable={true}
                                            scrollWithin={true}
                                        />
                                    </div>
                                )}{" "}
                            </div>
                        )}
                    </div>

                    {!isExpanded && !hideInlineExpand && ( // Also hide the bottom expand button
                        <div className="absolute left-1/2 bottom-4">
                            <Button
                                btnText="Expand Canvas"
                                btnClassname="w-full !text-g-75 !border-slate-200 !py-2.5 !px-4 !rounded-[20px] !shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.03),0px_12px_16px_-4px_rgba(16,24,40,0.08)]"
                                icon={<i className="ri-picture-in-picture-exit-line text-g-75"></i>}
                                onClick={handleExpand}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SalesCanvas;
